package br.com.heyagenda.data.repository

import br.com.heyagenda.data.remote.FirebaseAuthDataSource
import br.com.heyagenda.data.remote.FirestoreAvailabilityDataSource
import br.com.heyagenda.domain.model.DateScheduleOverride
import br.com.heyagenda.domain.model.DayOff
import br.com.heyagenda.domain.model.DaySchedule
import br.com.heyagenda.domain.model.ProfessionalAvailability
import br.com.heyagenda.domain.repository.AvailabilityRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.map

class AvailabilityRepositoryImpl(
    private val firestoreDataSource: FirestoreAvailabilityDataSource,
    private val authDataSource: FirebaseAuthDataSource
) : AvailabilityRepository {

    private val localAvailability = MutableStateFlow(ProfessionalAvailability())

    override fun getAvailability(): Flow<ProfessionalAvailability> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            println(">>> [AvailabilityRepo] User UID is null. Returning default availability.")
            return localAvailability.asStateFlow()
        }

        return firestoreDataSource.observeAvailability(uid)
            .map { remote ->
                remote ?: localAvailability.value
            }
    }

    override suspend fun updateDaySchedule(daySchedule: DaySchedule): Result<Unit> {
        val current = localAvailability.value
        val updatedSchedule = current.weeklySchedule.map { item ->
            if (item.dayOfWeek == daySchedule.dayOfWeek) daySchedule else item
        }
        val updatedAvailability = current.copy(weeklySchedule = updatedSchedule)
        localAvailability.value = updatedAvailability

        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            firestoreDataSource.saveAvailability(uid, updatedAvailability)
        }
        return Result.success(Unit)
    }

    override suspend fun addDayOff(dayOff: DayOff): Result<Unit> {
        val current = localAvailability.value
        val updatedDaysOff = current.daysOff.filterNot { it.id == dayOff.id } + dayOff
        val updatedAvailability = current.copy(daysOff = updatedDaysOff)
        localAvailability.value = updatedAvailability

        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            firestoreDataSource.saveAvailability(uid, updatedAvailability)
        }
        return Result.success(Unit)
    }

    override suspend fun removeDayOff(dayOffId: String): Result<Unit> {
        val current = localAvailability.value
        val updatedDaysOff = current.daysOff.filterNot { it.id == dayOffId }
        val updatedAvailability = current.copy(daysOff = updatedDaysOff)
        localAvailability.value = updatedAvailability

        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            firestoreDataSource.saveAvailability(uid, updatedAvailability)
        }
        return Result.success(Unit)
    }

    override suspend fun saveDateOverride(override: DateScheduleOverride): Result<Unit> {
        val current = localAvailability.value
        val updatedOverrides = current.dateOverrides.filterNot { it.id == override.id } + override
        val updatedAvailability = current.copy(dateOverrides = updatedOverrides)
        localAvailability.value = updatedAvailability

        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            firestoreDataSource.saveAvailability(uid, updatedAvailability)
        }
        return Result.success(Unit)
    }

    override suspend fun removeDateOverride(overrideId: String): Result<Unit> {
        val current = localAvailability.value
        val updatedOverrides = current.dateOverrides.filterNot { it.id == overrideId }
        val updatedAvailability = current.copy(dateOverrides = updatedOverrides)
        localAvailability.value = updatedAvailability

        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            firestoreDataSource.saveAvailability(uid, updatedAvailability)
        }
        return Result.success(Unit)
    }

    override suspend fun getAvailableTimeSlots(dateIso: String, serviceDurationMinutes: Int): List<String> {
        val availability = localAvailability.value

        // 1. Check if dateIso is a blocked DayOff or Holiday
        val isBlocked = availability.daysOff.any { dayOff ->
            dayOff.dateIso == dateIso || (dayOff.isRecurringYearly && dateIso.endsWith(dayOff.dateIso.takeLast(5)))
        }

        if (isBlocked) {
            println(">>> [AvailabilityRepo] Date $dateIso is a blocked DayOff/Holiday. Returning 0 slots.")
            return emptyList()
        }

        // 2. Check for date-specific override
        val dateOverride = availability.dateOverrides.find { it.dateIso == dateIso }
        val startTimeStr: String
        val endTimeStr: String
        val breakStartStr: String?
        val breakEndStr: String?

        if (dateOverride != null) {
            if (!dateOverride.isOpen) {
                return emptyList()
            }
            startTimeStr = dateOverride.startTime
            endTimeStr = dateOverride.endTime
            breakStartStr = dateOverride.breakStart
            breakEndStr = dateOverride.breakEnd
        } else {
            startTimeStr = "08:00"
            endTimeStr = "18:00"
            breakStartStr = "12:00"
            breakEndStr = "13:00"
        }

        fun parseMinutes(time: String, default: Int): Int {
            return try {
                val parts = time.split(":")
                parts[0].toInt() * 60 + parts[1].toInt()
            } catch (_: Throwable) {
                default
            }
        }

        var currentMinutes = parseMinutes(startTimeStr, 8 * 60)
        val endDayMinutes = parseMinutes(endTimeStr, 18 * 60)
        val breakStartMin = breakStartStr?.let { parseMinutes(it, 12 * 60) }
        val breakEndMin = breakEndStr?.let { parseMinutes(it, 13 * 60) }
        val step = maxOf(30, serviceDurationMinutes)

        val slots = mutableListOf<String>()
        while (currentMinutes + serviceDurationMinutes <= endDayMinutes) {
            val inBreak = if (breakStartMin != null && breakEndMin != null) {
                currentMinutes < breakEndMin && (currentMinutes + serviceDurationMinutes) > breakStartMin
            } else {
                false
            }

            if (!inBreak) {
                val hour = (currentMinutes / 60).toString().padStart(2, '0')
                val min = (currentMinutes % 60).toString().padStart(2, '0')
                slots.add("$hour:$min")
            }
            currentMinutes += step
        }

        return slots
    }
}
